import React, { useMemo, useState } from 'react';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { Dayjs } from 'dayjs';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import Tooltip from '@mui/material/Tooltip';
import DateRangeDialog from './dialog';
import {
  generateMonthPeriods,
  generateDayPeriods,
  generateCustomPeriodPlaceholder,
  IDatePeriod,
  generateCustomPeriod,
} from './utils';

export type { IDatePeriod } from './utils';

interface PeriodSelectorProps {
  changePeriod: (period: IDatePeriod) => void;
  selectedPeriod: IDatePeriod | undefined;
}

const PeriodSelector = function ({ changePeriod, selectedPeriod }: PeriodSelectorProps) {
  const [open, setOpen] = useState(false);
  const monthPeriods = useMemo(() => generateMonthPeriods(), [selectedPeriod]);
  const dayPeriods = useMemo(() => generateDayPeriods(), [selectedPeriod]);
  const customPeriod = useMemo(() => generateCustomPeriodPlaceholder(), [selectedPeriod]);

  const datePeriods = [...dayPeriods, customPeriod, ...monthPeriods];

  const onPeriodChange = (event: SelectChangeEvent<string>) => {
    const key = event.target.value;
    const period = datePeriods.find((p) => p.key === key);

    // The custom case is a bit special. We want to show a date picker for that
    // and ask the user to select a custom date.
    if (key === 'custom') {
      setOpen(true);
    } else {
      changePeriod(period);
    }
  };

  const setRange = (fromDate: Dayjs, toDate: Dayjs) => {
    const period = generateCustomPeriod(fromDate, toDate);
    changePeriod(period);
  };

  return (
    <>
      <FormControl sx={{ minWidth: '200px' }} data-test-id="period-selector">
        <InputLabel id="period-label">Period</InputLabel>
        <Select labelId="period-label" defaultValue="" label="Period" onChange={onPeriodChange}>
          {datePeriods.map((date) => (
            <MenuItem key={date.key} data-test-id="period-selector-item" value={date.key}>
              {date.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selectedPeriod?.key === 'custom' && (
        <Tooltip title="Redigera datumperiod">
          <IconButton size="small" color="primary" onClick={() => setOpen(true)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      )}
      <DateRangeDialog open={open} setOpen={setOpen} setRange={setRange} />
    </>
  );
};

export default PeriodSelector;
